/*
  AeroClaus: the one custom script. Deferred, no dependencies. The page works without it:
  prices, buy buttons and neutral delivery text are server-rendered; this layers on
  real delivery dates, variant sync, video loading, the sticky bar and the cart-drawer swap.

  QA: add ?ac_today=2026-12-10 to any URL to preview the date logic for another day.
*/
(() => {
  'use strict';
  document.documentElement.classList.add('ac-js');

  /* ---------- Dates: ETA, cutoff, season ---------- */
  const cfgEl = document.getElementById('ac-config');
  let cfg = {};
  try { cfg = cfgEl ? JSON.parse(cfgEl.textContent) : {}; } catch (e) { cfg = {}; }

  const parseDate = (s) => {
    const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec((s || '').trim());
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
  };
  const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const today = (() => {
    const d = parseDate(new URLSearchParams(location.search).get('ac_today')) || new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  })();
  const cutoff = parseDate(cfg.cutoff);
  const christmas = parseDate(cfg.christmas);
  const minDays = parseInt(cfg.minDays, 10) || 7;
  const maxDays = parseInt(cfg.maxDays, 10) || 14;
  const etaFrom = addDays(today, minDays);
  const etaTo = addDays(today, maxDays);

  // 'pre' only while the cutoff hasn't passed AND the latest estimate still lands before Christmas.
  let season = 'post';
  if (christmas && today > christmas) season = 'xmas';
  else if (cutoff && today <= cutoff && (!christmas || etaTo < christmas)) season = 'pre';

  const cutoffText = cutoff ? fmt(cutoff) : '';
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  function applyDates(root) {
    root.querySelectorAll('[data-ac-eta-text]:not([data-ac-done])').forEach((el) => {
      const suffix = season === 'pre' ? cfg.preCutoff : season === 'post' ? cfg.postCutoff : cfg.postChristmas;
      el.innerHTML =
        esc(cfg.etaPrefix || 'Order today, arrives') +
        ' <strong>' + esc(fmt(etaFrom)) + ' – ' + esc(fmt(etaTo)) + '</strong>' +
        (suffix ? ' · ' + esc(suffix) : '');
      el.setAttribute('data-ac-done', '');
    });

    root.querySelectorAll('[data-ac-announce]:not([data-ac-done])').forEach((el) => {
      const text = el.dataset[season === 'pre' ? 'pre' : season === 'post' ? 'post' : 'xmas'];
      if (text) el.textContent = text.replace(/\[date\]/g, cutoffText);
      el.setAttribute('data-ac-done', '');
    });

    // Lines that belong to one season only (e.g. "Order by Dec 7 for Christmas").
    root.querySelectorAll('[data-ac-show-season]').forEach((el) => { el.hidden = el.dataset.acShowSeason !== season; });

    // FAQ answers written per season (server picks one; the page may be cached, so re-check here).
    root.querySelectorAll('[data-ac-season-answer]').forEach((el) => {
      el.querySelectorAll('[data-season]').forEach((a) => { a.hidden = a.dataset.season !== season; });
    });

    if (season === 'pre') {
      root.querySelectorAll('[data-ac-seasonal]:not([data-ac-done])').forEach((el) => {
        const main = el.querySelector('[data-ac-seasonal-main]');
        const sub = el.querySelector('[data-ac-seasonal-sub]');
        if (main && el.dataset.pre) main.textContent = el.dataset.pre;
        if (sub && el.dataset.preSub) {
          sub.textContent = el.dataset.preSub.replace(/\[date\]/g, cutoffText);
          sub.hidden = false;
        }
        el.setAttribute('data-ac-done', '');
      });
    }
  }

  /* ---------- Buy forms: bundle cards → one hidden [name=id] ---------- */
  function initBuy(buy) {
    const form = buy.querySelector('form');
    if (!form || form.dataset.acReady) return;
    form.dataset.acReady = '1';
    const radios = Array.from(form.querySelectorAll('input[type="radio"][name="id"]'));
    if (!radios.length) return;
    // Note: form.id would return the <input name="id">, not the form's id attribute.
    const formId = form.getAttribute('id');

    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'id';
    hidden.value = (radios.find((r) => r.checked) || radios[0]).value;
    radios.forEach((r) => { r.name = 'ac-variant-' + formId; });
    form.prepend(hidden);

    const sync = (r) => {
      hidden.value = r.value;
      const targets = document.querySelectorAll('[data-ac-for="' + formId + '"]');
      buy.querySelectorAll('[data-ac-price]').forEach((el) => { el.textContent = r.dataset.price; });
      targets.forEach((t) => {
        t.querySelectorAll('[data-ac-price]').forEach((el) => { el.textContent = r.dataset.price; });
        t.querySelectorAll('[data-ac-variant-title]').forEach((el) => { el.textContent = r.dataset.title; });
      });
      hidden.dispatchEvent(new Event('change', { bubbles: true }));
    };
    radios.forEach((r) => r.addEventListener('change', () => sync(r)));
  }

  // Lets other sections (gift nudge, final CTA) pick a variant in the hero buy box.
  window.AeroClaus = window.AeroClaus || {};
  window.AeroClaus.selectVariant = (variantId, scrollTo) => {
    const radio = document.querySelector('[data-ac-hero] input[type="radio"][value="' + variantId + '"]');
    if (!radio) return false;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    if (scrollTo) radio.closest('[data-ac-buy]').scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  };

  /* ---------- Hero video: poster first, then the right-sized MP4 ---------- */
  function initVideo(wrap) {
    const video = wrap.querySelector('video');
    if (!video) return;
    const playBtn = wrap.querySelector('[data-ac-play]');
    const desktop = window.matchMedia('(min-width: 990px)').matches;
    const read = (a) => { try { return (JSON.parse(a || '[]') || []).filter(Boolean); } catch (e) { return []; } };
    let list = read(desktop ? video.dataset.sourcesDesktop : video.dataset.sourcesMobile);
    if (!list.length) list = read(desktop ? video.dataset.sourcesMobile : video.dataset.sourcesDesktop);
    if (!list.length) return;

    // Smallest rendition that's sharp enough for this screen (capped at 1080 wide on phones).
    const need = Math.min(wrap.clientWidth * (window.devicePixelRatio || 1), desktop ? 1920 : 1080);
    list.sort((a, b) => a.w - b.w);
    const pick = list.find((s) => s.w >= need * 0.9) || list[list.length - 1];

    let allowed = true;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = navigator.connection && navigator.connection.saveData;
    const showPlay = () => { if (playBtn) playBtn.hidden = false; };
    const start = () => {
      if (!video.src) video.src = pick.url;
      const p = video.play();
      if (p && p.catch) p.catch(() => { allowed = false; showPlay(); }); // autoplay blocked (some in-app browsers)
    };

    video.addEventListener('playing', () => {
      wrap.classList.add('is-playing');
      if (playBtn) playBtn.hidden = true;
    });
    if (playBtn) playBtn.addEventListener('click', () => { allowed = true; start(); });

    if (reduce || saveData) { allowed = false; showPlay(); return; }

    if (wrap.hasAttribute('data-ac-lazy') && 'IntersectionObserver' in window) {
      // Below-the-fold video: download only when it's about to scroll into view.
      const near = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { near.disconnect(); start(); }
      }, { rootMargin: '300px 0px' });
      near.observe(wrap);
    } else {
      // Hero: start after the page (and the poster, the LCP image) has loaded.
      const kick = () => (window.requestIdleCallback ? requestIdleCallback(start, { timeout: 1500 }) : setTimeout(start, 200));
      if (document.readyState === 'complete') kick();
      else window.addEventListener('load', kick, { once: true });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!video.src) return;
          if (e.isIntersecting) { if (allowed && video.paused) video.play().catch(() => {}); }
          else if (!video.paused) video.pause();
        });
      }, { threshold: 0.15 }).observe(wrap);
    }
  }

  /* ---------- Sticky add-to-cart (phones) ---------- */
  function initSticky() {
    const sticky = document.querySelector('[data-ac-sticky]');
    const atc = document.querySelector('[data-ac-hero] .ac-buy__atc');
    if (!sticky || !atc || !('IntersectionObserver' in window)) return;

    let atcVisible = true;
    const finalsVisible = new Set();
    const cookieBanner = () => {
      const b = document.getElementById('shopify-pc__banner');
      return b && b.offsetParent !== null && getComputedStyle(b).visibility !== 'hidden';
    };
    const update = () => {
      const show = !atcVisible && finalsVisible.size === 0 && !cookieBanner();
      sticky.classList.toggle('is-visible', show);
      document.body.classList.toggle('ac-sticky-on', show);
      if (show) { sticky.removeAttribute('inert'); sticky.setAttribute('aria-hidden', 'false'); }
      else { sticky.setAttribute('inert', ''); sticky.setAttribute('aria-hidden', 'true'); }
    };

    new IntersectionObserver((entries) => {
      entries.forEach((e) => { atcVisible = e.isIntersecting; });
      update();
    }).observe(atc);

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) finalsVisible.add(e.target); else finalsVisible.delete(e.target); });
      update();
    });
    document.querySelectorAll('[data-ac-final]').forEach((el) => io.observe(el));

    // The cookie banner is injected late; step aside while it's showing.
    new MutationObserver(update).observe(document.body, { childList: true });
    document.addEventListener('visibilitychange', update);
  }

  /* ---------- "Choose 2 Sleighs" buttons: select it in the hero buy box and scroll there ---------- */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest && e.target.closest('form[data-ac-select-variant]');
    if (!form) return;
    if (window.AeroClaus.selectVariant(form.dataset.acSelectVariant, true)) e.preventDefault();
    // Otherwise (no hero on this page) the form falls through and adds it to the cart.
  });

  /* ---------- Cart drawer: one-tap swap to 2 Sleighs ---------- */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest && e.target.closest('form[data-ac-swap]');
    const drawer = document.querySelector('cart-drawer');
    if (!form || !drawer || !window.fetch) return;
    e.preventDefault();
    const btn = form.querySelector('button');
    if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
    const updates = {};
    updates[form.dataset.from] = 0;
    updates[form.dataset.to] = 1;
    fetch((window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : '/cart/update') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections: drawer.getSectionsToRender().map((s) => s.id),
        sections_url: location.pathname,
      }),
    })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((cart) => {
        drawer.renderContents({ id: form.dataset.to, sections: cart.sections });
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ac-swap', cartData: cart });
        }
      })
      .catch(() => form.submit());
  });

  /* ---------- Deferred below-the-fold images (snippets/ac-defer.liquid) ---------- */
  function loadImg(img) {
    if (img.dataset.acSrcset) img.srcset = img.dataset.acSrcset;
    if (img.dataset.acSrc) img.src = img.dataset.acSrc;
    img.removeAttribute('data-ac-defer');
  }
  function initDefer(root) {
    const imgs = root.querySelectorAll('img[data-ac-defer]');
    if (!imgs.length) return;
    if (!('IntersectionObserver' in window)) { imgs.forEach(loadImg); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { loadImg(e.target); io.unobserve(e.target); } });
    }, { rootMargin: '600px 0px' });
    imgs.forEach((img) => io.observe(img));
  }

  /* ---------- Gentle reveal on scroll ---------- */
  function initReveal() {
    const els = document.querySelectorAll('[data-ac-reveal]');
    if (!els.length || !('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -10% 0px' });
    els.forEach((el) => { el.classList.add('ac-reveal'); io.observe(el); });
  }

  /* ---------- Header menu: close on link click, outside click, Escape ---------- */
  function initMenu() {
    const menu = document.querySelector('[data-ac-menu]');
    if (!menu || menu.dataset.acReady) return;
    menu.dataset.acReady = '1';
    const close = () => { menu.open = false; };
    menu.addEventListener('click', (e) => { if (e.target.closest('a')) close(); });
    document.addEventListener('click', (e) => { if (menu.open && !menu.contains(e.target)) close(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.open) { close(); menu.querySelector('summary').focus(); }
    });
  }

  /* ---------- Boot ---------- */
  function boot() {
    applyDates(document);
    document.querySelectorAll('[data-ac-buy]').forEach(initBuy);
    document.querySelectorAll('[data-ac-video]').forEach(initVideo);
    initSticky();
    initMenu();
    initReveal();
    initDefer(document);

    // Cart drawer contents are re-rendered after every change: refill dates there.
    const drawer = document.querySelector('cart-drawer');
    if (drawer) new MutationObserver(() => applyDates(drawer)).observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: re-run when a section is re-rendered.
  document.addEventListener('shopify:section:load', (e) => {
    applyDates(e.target);
    e.target.querySelectorAll('[data-ac-buy]').forEach(initBuy);
    e.target.querySelectorAll('[data-ac-video]').forEach(initVideo);
    initDefer(e.target);
    initSticky();
  });
})();
